import { useIsMobile } from '@cortex/ui';
import { SlidersHorizontal } from 'lucide-react';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import * as React from 'react';
import { LogoutIcon, ProfileIcon, ThemeIcon } from '../modules/platform/index.ts';

export const MENU_ITEM_CLASS =
  'flex items-center gap-2 h-[30px] px-4 text-sm font-medium text-nav-active-hover cursor-pointer outline-none select-none transition-colors data-[highlighted]:text-nav-active first:mt-[10px] last:mb-[10px]';

export interface UserMenuProps {
  initials: string;
  fullName: string;
  simple: boolean;
  theme: 'light' | 'dark';
  onToggleView: () => void;
  onToggleTheme: () => void;
  onProfile: () => void;
  onLogout: () => void;
}

/** The sidebar footer: avatar + name opening the view, theme, profile and log-out menu. */
export function UserMenu(props: UserMenuProps) {
  const { initials, fullName, simple, theme } = props;
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <div
        className="shrink-0 flex w-full cursor-pointer text-white hover:text-nav-hover transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <DropdownMenuPrimitive.Trigger asChild>
          <button
            type="button"
            data-testid="user-menu-trigger"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 h-9 px-3 max-w-full bg-transparent border-0 outline-none cursor-pointer"
          >
            <div className="size-6 rounded-full bg-nav-avatar-bg flex items-center justify-center shrink-0 text-white text-xs font-medium">
              {initials}
            </div>
            <span className="min-w-0 text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap text-left">
              {fullName}
            </span>
          </button>
        </DropdownMenuPrimitive.Trigger>
      </div>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          side={isMobile ? 'top' : 'right'}
          align={isMobile ? 'start' : 'end'}
          sideOffset={8}
          collisionPadding={8}
          data-testid="user-menu"
          className="z-50 min-w-[200px] rounded-[5px] bg-card-bg shadow-[0_8px_40px_0_var(--shadow-blue-md)] overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
        >
          <DropdownMenuPrimitive.Item
            onSelect={props.onToggleView}
            data-testid="ui-view-toggle"
            className={MENU_ITEM_CLASS}
          >
            <SlidersHorizontal size={24} className="shrink-0" style={{ color: 'var(--nav-active)' }} />
            {simple ? 'Advanced mode' : 'Standard mode'}
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item
            onSelect={props.onToggleTheme}
            data-testid="theme-toggle"
            className={MENU_ITEM_CLASS}
          >
            <ThemeIcon />
            {theme === 'dark' ? 'Light theme' : 'Dark theme'}
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item onSelect={props.onProfile} className={MENU_ITEM_CLASS}>
            <ProfileIcon />
            User Profile
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item
            onSelect={props.onLogout}
            data-testid="logout"
            className={MENU_ITEM_CLASS}
          >
            <LogoutIcon />
            Log Out
          </DropdownMenuPrimitive.Item>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
